import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Property } from "@/types/property";
import { PropertyContract } from "@/hooks/useContracts";
import { Organization } from "@/hooks/useOrganizations";
import { useMemo } from "react";

interface DashboardMobileFiltersProps {
  handlePropertyChange: (value: string) => void;
  monthOptions: { value: string; label: string }[];
  properties: Property[];
  organizations: Organization[];
  contracts: PropertyContract[];
  selectedMonth: string;
  selectedProperties: string[];
  selectedOrganizations: string[];
  selectedYear: string;
  setSelectedMonth: React.Dispatch<React.SetStateAction<string>>;
  setSelectedYear: React.Dispatch<React.SetStateAction<string>>;
  setSelectedOrganizations: React.Dispatch<React.SetStateAction<string[]>>;
  yearOptions: number[];
}

const DashboardMobileFilters = ({
  handlePropertyChange,
  monthOptions,
  properties,
  organizations,
  contracts,
  selectedMonth,
  selectedProperties,
  selectedOrganizations,
  selectedYear,
  setSelectedMonth,
  setSelectedYear,
  setSelectedOrganizations,
  yearOptions,
}: DashboardMobileFiltersProps) => {
  
  // Filtrar propiedades basadas en organizaciones seleccionadas (cascada)
  const filteredProperties = useMemo(() => {
    if (selectedOrganizations.length === 0) return properties;
    
    const activeContracts = contracts?.filter(c => c.status === 'active') || [];
    const propertyIdsInOrgs = activeContracts
      .filter(c => selectedOrganizations.includes(c.organization_id || ''))
      .map(c => c.property_id);
    
    return properties.filter(p => propertyIdsInOrgs.includes(p.id));
  }, [properties, contracts, selectedOrganizations]);
  
  const handleOrganizationChange = (value: string) => {
    if (value === "all") {
      setSelectedOrganizations([]);
    } else {
      setSelectedOrganizations([value]);
    }
  };

  return (
    <>
      <div className="flex gap-2 w-full">
        <Select value={selectedYear} onValueChange={setSelectedYear}>
          <SelectTrigger className="w-20 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors border-0 text-foreground">
            <SelectValue />
          </SelectTrigger>

          <SelectContent>
            {yearOptions.map((year) => (
              <SelectItem key={year} value={year.toString()}>
                {year}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={selectedMonth} onValueChange={setSelectedMonth}>
          <SelectTrigger className="flex-1 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors border-0 text-foreground">
            <SelectValue />
          </SelectTrigger>

          <SelectContent>
            {monthOptions.map((month) => (
              <SelectItem key={month.value} value={month.value}>
                {month.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="w-full">
        <Select
          value={
            selectedOrganizations.length === 0 ? "all" : selectedOrganizations[0]
          }
          onValueChange={handleOrganizationChange}
        >
          <SelectTrigger className="w-full rounded-full bg-gray-100 hover:bg-gray-200 transition-colors border-0 text-foreground">
            <SelectValue placeholder="Seleccionar organización" />
          </SelectTrigger>

          <SelectContent>
            <SelectItem value="all">Todas las organizaciones</SelectItem>
            {organizations?.map((org) => (
              <SelectItem key={org.id} value={org.id}>
                {org.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="w-full">
        <Select
          value={
            selectedProperties.length === 0 ? "all" : selectedProperties[0]
          }
          onValueChange={handlePropertyChange}
        >
          <SelectTrigger className="w-full rounded-full bg-gray-100 hover:bg-gray-200 transition-colors border-0 text-foreground">
            <SelectValue placeholder="Seleccionar propiedad" />
          </SelectTrigger>

          <SelectContent>
            <SelectItem value="all">Todas las propiedades</SelectItem>
            {filteredProperties?.map((property) => (
              <SelectItem key={property.id} value={property.id}>
                {property.shortName || property.title}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </>
  );
};

export default DashboardMobileFilters;
